import { useState } from 'react'
import { formatDue, listAssignments, listSubmissions, submitAssignment } from '@/lib/api'
import { useAsyncData } from '@/lib/useAsyncData'

export function StudentAssignmentsPage() {
  const assignments = useAsyncData(listAssignments, [])
  const submissions = useAsyncData(listSubmissions, [])
  const [busyId, setBusyId] = useState<number | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const submitted = new Map((submissions.data || []).map((s) => [s.assignment, s]))

  async function onSubmit(assignmentId: number, file?: File | null) {
    setBusyId(assignmentId)
    setMessage(null)
    try {
      await submitAssignment(assignmentId, file || undefined)
      setMessage('Submission saved.')
      await submissions.reload()
    } catch (e) {
      setMessage(e instanceof Error ? e.message : 'Submit failed')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="space-y-6 p-8">
      <h1 className="text-2xl font-bold text-slate-800">Assignments</h1>
      {message && <p className="rounded-xl bg-slate-100 px-4 py-3 text-sm text-slate-700">{message}</p>}
      {assignments.loading && <p className="text-sm text-slate-500">Loading…</p>}
      {assignments.error && (
        <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{assignments.error}</p>
      )}
      <div className="space-y-4">
        {(assignments.data || []).map((a) => {
          const sub = submitted.get(a.id)
          return (
            <article key={a.id} className="rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-base font-bold text-slate-800">{a.title}</h2>
                  <p className="text-xs text-slate-500">
                    {a.course_title} ({a.course_code}) · Due {formatDue(a.due_date)} · {a.max_marks} marks
                  </p>
                </div>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-bold text-slate-600 uppercase">
                  {sub ? 'Submitted' : 'Open'}
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-600 whitespace-pre-wrap">{a.instructions}</p>
              {!sub && (
                <div className="mt-4 flex flex-wrap items-center gap-3">
                  <input
                    id={`file-${a.id}`}
                    type="file"
                    className="text-xs text-slate-600"
                    onChange={(ev) => {
                      const file = ev.target.files?.[0]
                      void onSubmit(a.id, file)
                    }}
                  />
                  <button
                    type="button"
                    disabled={busyId === a.id}
                    onClick={() => void onSubmit(a.id)}
                    className="rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold text-white disabled:bg-slate-300"
                  >
                    {busyId === a.id ? 'Submitting…' : 'Mark submitted'}
                  </button>
                </div>
              )}
              {sub?.marks_obtained != null && (
                <p className="mt-3 text-xs font-semibold text-emerald-700">
                  Marks: {sub.marks_obtained}
                  {sub.feedback ? ` · ${sub.feedback}` : ''}
                </p>
              )}
            </article>
          )
        })}
      </div>
      {!assignments.loading && (assignments.data || []).length === 0 && (
        <p className="text-sm text-slate-500">No assignments yet.</p>
      )}
    </div>
  )
}
